import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { convertToFullWidth } from '@/components/logics/Util'

export const useRegionStore = defineStore('address', {
  state: () => ({
    // 住所検索関連
    isAddressFetching: false,
    lastFetchedAddress: null,
    currentPostalCode: '',

    // 都道府県情報関連
    prefectures: [],
    // 都道府県マップ
    prefectureCodeMap: {}, // コード → 都道府県オブジェクト
    prefectureNameMap: {}, // 名前 → 都道府県オブジェクト

    // 労働局情報
    laborData: [],
    // 労働局・監督署マップ
    bureauCodeMap: {}, // 労働局コード → 労働局オブジェクト
    bureauNameMap: {}, // 労働局名 → 労働局オブジェクト
    officeCodeMap: {}, // 監督署コード → 監督署オブジェクト (親労働局の情報も含む)
    officeNameMap: {}, // 監督署名 → 監督署オブジェクト (親労働局の情報も含む)
  }),

  actions: {
    // 都道府県情報を取得
    async getPrefectures() {
      // すでに取得済みならスキップ
      if (this.prefectures.length > 0) {
        return this.prefectures
      }

      try {
        // 既存のエンドポイントを使用
        const response = await apiService.get('/api/prefectures')
        this.prefectures = response.prefectures

        // ルックアップマップを生成
        this._generatePrefectureMaps()

        return this.prefectures
      } catch (e) {
        return []
      }
    },

    // 都道府県マップを生成
    _generatePrefectureMaps() {
      // コードマップと名前マップを初期化
      const codeMap = {}
      const nameMap = {}

      // 各都道府県をマップに追加
      this.prefectures.forEach(prefecture => {
        codeMap[prefecture.code] = prefecture
        nameMap[prefecture.name] = prefecture
      })

      // stateに保存
      this.prefectureCodeMap = codeMap
      this.prefectureNameMap = nameMap
    },

    // 郵便番号から住所を取得
    async getAddressWithPostalCode(postalCode) {
      // すでに同じ郵便番号でフェッチしていないか確認
      if (this.currentPostalCode === postalCode) {
        return this.lastFetchedAddress
      }
      this.currentPostalCode = postalCode
      this.isAddressFetching = true

      try {
        // 郵便番号のバリデーション
        if (!postalCode || !postalCode.match(/^\d{7}$|^\d{3}-\d{4}$/)) {
          return false
        }

        // ハイフンを除去
        const cleanedPostalCode = postalCode.replace('-', '')

        // zipcloud APIを直接呼び出し（fetch API使用）
        const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${cleanedPostalCode}`)

        if (!response.ok) {
          return false
        }

        const data = await response.json()

        // APIからのレスポンスをチェック
        if (data.status !== 200) {
          return false
        }

        // 検索結果が存在しない場合
        if (!data.results || data.results.length === 0) {
          return false
        }

        // 住所情報を整形
        const addressData = data.results[0]
        const formattedAddress = {
          postal_code: addressData.zipcode,
          prefecture: addressData.address1,
          address1: addressData.address2 + addressData.address3,
          prefecture_kana: convertToFullWidth(addressData.kana1),
          address1_kana: convertToFullWidth(addressData.kana2 + addressData.kana3),
        }

        this.lastFetchedAddress = formattedAddress
        return this.lastFetchedAddress
      } catch (e) {
        return false
      } finally {
        this.isAddressFetching = false
      }
    },

    // 労働局情報を取得
    async getLaborData() {
      // すでに取得済みならスキップ
      if (this.laborData.length > 0) {
        return this.laborData
      }

      try {
        const response = await apiService.get('/api/labor-data')
        // すべてのコードを文字列に統一
        this.laborData = response.laborData.map(bureau => ({
          ...bureau,
          code: String(bureau.code),
          offices: bureau.offices.map(office => ({
            ...office,
            code: String(office.code),
          })),
        }))
        // ルックアップマップを生成
        this._generateLaborMaps()

        return this.laborData
      } catch (e) {
        return []
      }
    },

    // 労働局・監督署マップを生成
    _generateLaborMaps() {
      // 各種マップを初期化
      const bureauCodeMap = {}
      const bureauNameMap = {}
      const officeCodeMap = {}
      const officeNameMap = {}

      // 各労働局とその監督署をマップに追加
      this.laborData.forEach(bureau => {
        // 労働局マップに追加
        bureauCodeMap[bureau.code] = bureau
        bureauNameMap[bureau.name] = bureau

        // 各監督署をマップに追加
        bureau.offices.forEach(office => {
          // 監督署オブジェクトに親労働局の情報を追加
          const officeWithBureau = {
            ...office,
            bureau: {
              code: String(bureau.code),
              name: bureau.name,
            },
          }

          officeCodeMap[String(office.code)] = officeWithBureau
          officeNameMap[office.name] = officeWithBureau
        })
      })

      // stateに保存
      this.bureauCodeMap = bureauCodeMap
      this.bureauNameMap = bureauNameMap
      this.officeCodeMap = officeCodeMap
      this.officeNameMap = officeNameMap
    },

    // 結果をリセット
    resetAddress() {
      this.lastFetchedAddress = null
      this.currentPostalCode = ''
    },

    // 都道府県名から都道府県コードを取得するgetter
    getPrefectureCodeByName(prefectureName) {
      if (!prefectureName) return ''
      // 都道府県名から都道府県コードを取得
      const prefecture = this.prefectureNameMap[prefectureName]
      if (prefecture) {
        return prefecture.code
      } else {
        return ''
      }
    },
  },

  getters: {
    // 都道府県名の配列を取得
    prefectureNames() {
      return this.prefectures.map(pref => pref.name)
    },

    // 都道府県コードと都道府県名のマッピングを取得
    prefectureCodesMap() {
      const map = {}
      Object.values(this.prefectureCodeMap).forEach(pref => {
        map[pref.code] = pref.name
      })
      return map
    },

    // 都道府県名と都道府県コードのマッピングを取得
    prefectureNameToCodeMap() {
      const map = {}
      Object.values(this.prefectureNameMap).forEach(pref => {
        map[pref.name] = pref.code
      })
      return map
    },

    // 都道府県名と都道府県カナのマッピングを取得
    prefectureKanaMap() {
      const map = {}
      Object.values(this.prefectureNameMap).forEach(pref => {
        map[pref.name] = pref.kana
      })
      return map
    },

    // 労働局名の一覧を取得するgetter
    bureauOptions() {
      return this.laborData.map(bureau => ({
        name: bureau.name,
        code: bureau.code,
      }))
    },

    // 選択された労働局に基づく監督署の一覧を取得するgetter
    officeOptions() {
      // 選択された労働局のコードまたは名前を受け取る関数
      return selectedBureau => {
        if (!selectedBureau) return []

        // 選択された労働局を検索（マップを使用）
        const bureau = this.bureauCodeMap[String(selectedBureau)] || this.bureauNameMap[selectedBureau]

        // 見つからない場合は空配列を返す
        if (!bureau) return []

        // 労働局に紐づく監督署一覧を返す
        return bureau.offices.map(office => ({
          name: office.name,
          code: String(office.code), // コードを文字列に変換
        }))
      }
    },

    // 監督署コードから監督署名を取得するgetter
    getOfficeNameByCode() {
      return code => {
        if (!code) return ''

        // マップから直接取得
        const office = this.officeCodeMap[code]
        return office ? office.name : ''
      }
    },

    // 監督署名から監督署コードを取得するgetter
    getOfficeCodeByName() {
      return name => {
        if (!name) return ''

        // マップから直接取得
        const office = this.officeNameMap[name]
        return office ? office.code : ''
      }
    },
  },

  persist: {
    paths: ['prefectures', 'laborData', 'prefectureCodeMap', 'prefectureNameMap', 'bureauCodeMap', 'bureauNameMap', 'officeCodeMap', 'officeNameMap'],
  },
})
